import React, { useState, useEffect, createContext, useContext } from "react";
import { auth, db } from "../firebase.js"; // Firebase Auth & Firestore
import { onAuthStateChanged, signOut } from "firebase/auth"; // Firebase Auth functions
import { getDoc, doc } from "firebase/firestore"; // Firestore functions
import { getStudentById } from "../services/studentService.js"; // Service to check students collection
import { getTeacherById } from "../services/teacherService.js"; // Service to check teachers collection

// Create the Authentication Context
const AuthContext = createContext(null);

// Custom hook to easily access the Auth Context
export const useAuth = () => {
  return useContext(AuthContext);
};

// AuthProvider component: Wraps the application and manages auth state
export const AuthProvider = ({ children }) => {
  // State for the currently logged-in user (null if not logged in)
  const [currentUser, setCurrentUser] = useState(null);
  // State to track if the initial authentication check is complete
  const [loading, setLoading] = useState(true);

  // Effect hook to listen for changes in Firebase Authentication state
  useEffect(() => {
    // onAuthStateChanged returns an unsubscribe function
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // User is signed in via Firebase Auth.
        try {
          // Now, determine the user's role and fetch detailed data.
          let userData = null;
          let userRole = 'unknown'; // Default role if not found in specific collections

          // 1. Check if the user exists in the 'students' collection
          const studentData = await getStudentById(user.uid).catch(() => null); // Use .catch to prevent promise rejection from stopping the flow
          if (studentData) {
             // If found as student, construct user object with role
             userData = { uid: user.uid, role: 'student', ...studentData };
             userRole = 'student';
             console.log(`AuthProvider: User ${user.uid} identified as student.`);
          } else {
             // 2. If not a student, check if the user exists in the 'teachers' collection
             const teacherData = await getTeacherById(user.uid).catch(() => null);
             if (teacherData) {
                 // If found as teacher, construct user object with role
                 userData = { uid: user.uid, role: 'teacher', ...teacherData };
                 userRole = 'teacher';
                 console.log(`AuthProvider: User ${user.uid} identified as teacher.`);
             }
          }

          // 3. Check a generic 'users' collection for other roles (e.g., Admin)
          if (!userData) {
              const genericUserDoc = await getDoc(doc(db, "users", user.uid));
               if (genericUserDoc.exists()) {
                   const genericData = genericUserDoc.data();
                   // Ensure the role from the document is used, default if missing
                   const roleFromDoc = genericData.role || 'reader';
                   userData = { uid: user.uid, role: roleFromDoc, ...genericData };
                   userRole = roleFromDoc;
                   console.log(`AuthProvider: User ${user.uid} identified as ${userRole} from 'users' collection.`);
               }
          }

          // Set the currentUser state based on fetched data
          if (userData) {
            setCurrentUser(userData);
          } else {
            // User is authenticated with Firebase Auth but doesn't have a matching record
            // in students, teachers (or users) collections.
            console.warn(`AuthProvider: User ${user.uid} (${user.email}) logged in via Auth, but no corresponding Firestore record found in relevant collections.`);
            // Decide how to handle this: Assign a default role, show an error, or log out.
            // Example: Set a minimal user object with an 'unknown' role
            setCurrentUser({ uid: user.uid, email: user.email, role: 'unknown' });
            // Example: Force logout if a specific role is always required
            // await signOut(auth);
            // setCurrentUser(null);
          }

        } catch (error) {
          // Handle errors during Firestore data fetching
          console.error("AuthProvider: Error fetching user data from Firestore:", error);
          setCurrentUser(null); // Ensure user is logged out in context on error
        }
      } else {
        // No user is signed in via Firebase Auth.
        console.log("AuthProvider: No user signed in.");
        setCurrentUser(null);
      }
      // Finished initial auth check
      setLoading(false);
    });

    // Cleanup function: Unsubscribe from the listener when the component unmounts
    return unsubscribe;
  }, []); // Empty dependency array ensures this effect runs only once on mount

  // Function to log out the user using Firebase Auth
  const logoutUser = async () => {
    try {
      await signOut(auth); // Sign out from Firebase
      setCurrentUser(null); // Clear user state in the context
      console.log("AuthProvider: User logged out.");
    } catch (error) {
      console.error("AuthProvider: Error logging out:", error);
    }
  };

  // Display a loading indicator while the initial auth check is in progress
  if (loading) {
    // You can replace this with a more sophisticated loading spinner component
    return <div>Loading authentication...</div>;
  }

  // Provide the authentication context (currentUser, loading state, and logout function)
  // to the rest of the application
  return (
    <AuthContext.Provider value={{ currentUser, loading, setCurrentUser, logoutUser }}>
      {children}
    </AuthContext.Provider>
  );
};
